import { autoinject, observable, BindingEngine } from "aurelia-framework";
import { ZeroHarmPartnersClientService } from "../../../../../../services/modules/zeroharm-partners.service";
import {
    PersistPartnerRequest,
    ZeroHarmPartnerSaveRequest,
} from "../../../../../../models/manage/zeroharmPartner.models";
import {
    NotificationClientService,
    NotificationType,
} from "../../../../../../services/basics/notification-client.service";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import {
    ValidationActivation,
    ValidationTypeEnum,
} from "../../../../../../client-validation-system/validations.models";

@autoinject()
export class AddZeroHarmPartnerDialog {
    private _reloadData: () => void;
    public partnerInEditUow: UnitOfWorkObserver;
    public logo: string;
    public fileName: string;
    public partnerId: number;

    @observable()
    public attachments: FileList;
    public partnerIMO: string;
    public upsellIMO: string;
    @observable()
    public partnerIMOList: FileList;
    @observable()
    public upsellIMOList: FileList;

    request: ZeroHarmPartnerSaveRequest = {
        description: null,
        identifier: null,
        isActive: false,
        logo: null,
        logoData: null,
        logoUrl: null,
        name: null,
        partnerIMOList: null,
        partnerUrl: null,
        upsellIMOList: null,
        zeroHarmSubCategoryId: null,
        entityType: EntityTypes.ZeroharmPartner,
        zeroHarmPartnerId: 0,
        allOtherUpsell: false,
    };

    constructor(
        private partnerService: ZeroHarmPartnersClientService,
        private bindingEngine: BindingEngine,
        private validationsClientService: ValidationsClientService,
        private dialogClientService: DialogClientService,
        private entityValidationsService: EntityValidationsService,
        private notificationClientService: NotificationClientService
    ) { }

    async activate(args: any) {
        if (args.id) {
            this.request = await this.partnerService.getPartner(args.id);

            if (this.partnerInEditUow) {
                this.partnerInEditUow.dispose();
            }
        }
        this.partnerInEditUow = UnitOfWorkObserver.getTrackingInstance(
            this.bindingEngine,
            this.request
        );
        this._reloadData = args.reloadData;
    }

    detached() {
        if (this.partnerInEditUow) {
            this.partnerInEditUow.dispose();
        }
        this.partnerInEditUow = null;
        this.request = null;
    }

    getValidator() {
        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "ZeroHarmPartner",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [
                {
                    errorMessage: "Name is required",
                    parameters: null,
                    property: "name",
                    validationType: ValidationTypeEnum.Required,
                },
                {
                    errorMessage: "Partner Url is required",
                    parameters: null,
                    property: "partnerUrl",
                    validationType: ValidationTypeEnum.Required,
                },
                {
                    errorMessage: "Sub Category is required",
                    parameters: null,
                    property: "zeroHarmSubCategoryId",
                    validationType: ValidationTypeEnum.Required,
                },
                {
                    errorMessage: "Partner Logo is required",
                    parameters: null,
                    property: "logoUrl",
                    validationType: ValidationTypeEnum.Required,
                }
            ],
        };

        return this.entityValidationsService.getValidator(
            this.request,
            rulesHolder
        );
    }

    close() {
        this.dialogClientService.hide();
    }

    async save() {
        const validators = this.getValidator();
        const validationResponse = await validators.validate();
        if (!validationResponse.valid) return;
        return new Promise<void>((resolve, reject) => {
            setTimeout(() => {
                const changes = this.partnerInEditUow.getChanges();
                this.partnerService
                    .save(<PersistPartnerRequest>{
                        changes: changes,
                        id: this.request.zeroHarmPartnerId,
                        logo: this.logo,
                        fileName: this.fileName,
                    })
                    .then((x) => {
                        if (x.validationResult && x.validationResult.hasError) {
                            this.validationsClientService.showValidationErrors(
                                x.validationResult
                            );
                            reject();
                        } else {
                            this.partnerInEditUow.clearChanges();
                            resolve();
                            this._reloadData();
                            this.dialogClientService.hide();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
            });
        });
    }

    partnerIMOListChanged() {
        if (this.partnerIMOList) {
            const file = this.partnerIMOList.item(0);
            if (file) {
                if (
                    file.name.endsWith(".csv") ||
                    file.name.endsWith(".xls") ||
                    file.name.endsWith(".xlsx")
                ) {
                    let reader = new FileReader();
                    const imos = [];
                    reader.onload = (e: any) => {
                        import('xlsx').then(XLSX => {
                            const data = e.target.result as string;
                            try {
                                const workbook = XLSX.read(data, { type: "binary" });
                                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                                const range = XLSX.utils.decode_range(sheet["!ref"]);

                                for (let R = range.s.r; R <= range.e.r; ++R) {
                                    for (let C = range.s.c; C <= range.e.c; ++C) {
                                        const cell_address = { c: C, r: R };
                                        /* if an A1-style address is needed, encode the address */
                                        const cell_ref = XLSX.utils.encode_cell(cell_address);

                                        if (sheet[cell_ref]) {
                                            const imo = sheet[cell_ref].v;

                                            if (
                                                imo &&
                                                (typeof imo === "number" || isFinite(Number(imo)))
                                            ) {
                                                imos.push(imo);
                                            }
                                        }
                                    }
                                }

                                this.request.partnerIMOList = imos.join(",");
                            } catch {
                                this.notificationClientService.show(
                                    "Error",
                                    "File cannot be opened due to incorrect format.",
                                    NotificationType.Error
                                );
                            }

                            reader.onload = null;
                            reader = null;
                        });
                    };

                    reader.readAsBinaryString(file);
                } else {
                    this.notificationClientService.show(
                        "Error",
                        "File cannot be opened due to incorrect format.",
                        NotificationType.Error
                    );
                }
            }
        }
    }

    upsellIMOListChanged() {
        if (this.upsellIMOList) {
            const file = this.upsellIMOList.item(0);
            if (file) {
                if (
                    file.name.endsWith(".csv") ||
                    file.name.endsWith(".xls") ||
                    file.name.endsWith(".xlsx")
                ) {
                    let reader = new FileReader();
                    const imos = [];
                    reader.onload = (e: any) => {
                        import('xlsx').then(XLSX => {
                            const data = e.target.result as string;
                            try {
                                const workbook = XLSX.read(data, { type: "binary" });
                                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                                const range = XLSX.utils.decode_range(sheet["!ref"]);

                                for (let R = range.s.r; R <= range.e.r; ++R) {
                                    for (let C = range.s.c; C <= range.e.c; ++C) {
                                        const cell_address = { c: C, r: R };
                                        /* if an A1-style address is needed, encode the address */
                                        const cell_ref = XLSX.utils.encode_cell(cell_address);

                                        if (sheet[cell_ref]) {
                                            const imo = sheet[cell_ref].v;

                                            if (
                                                imo &&
                                                (typeof imo === "number" || isFinite(Number(imo)))
                                            ) {
                                                imos.push(imo);
                                            }
                                        }
                                    }
                                }

                                this.request.upsellIMOList = imos.join(",");
                            } catch {
                                this.notificationClientService.show(
                                    "Error",
                                    "File cannot be opened due to incorrect format.",
                                    NotificationType.Error
                                );
                            }

                            reader.onload = null;
                            reader = null;
                        });

                    };

                    reader.readAsBinaryString(file);
                } else {
                    this.notificationClientService.show(
                        "Error",
                        "File cannot be opened due to incorrect format.",
                        NotificationType.Error
                    );
                }
            }
        }
    }

    attachmentsChanged() {
        if (this.attachments) {
            const file = this.attachments[0];
            if (file) {
                if (
                    file.name.endsWith(".png") ||
                    file.name.endsWith(".jpg") ||
                    file.name.endsWith(".ico")
                ) {
                    let reader = new FileReader();
                    reader.onload = (e: any) => {
                        this.logo = e.target.result as string;
                        this.fileName = file.name;
                        reader.onload = null;
                        reader = null;
                        this.request.logoUrl = URL.createObjectURL(file);
                    };

                    reader.readAsDataURL(file);
                } else {
                    this.attachments = null;
                    this.notificationClientService.show(
                        "Error",
                        "File cannot be uploaded  due to incorrect format.",
                        NotificationType.Error
                    );
                }
            }
        }
    }
}
